'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect } from 'react'
import { isTransientNetworkError } from '@/lib/is-transient-network-error'

/**
 * Reports an error caught by a Next.js error boundary (error.tsx /
 * global-error.tsx). Boundaries catch render errors, so without this they
 * never reach Sentry. The event goes through the same contract as every
 * other (lib/sentry/build-event.ts).
 *
 * #920: a dropped connection is not a code defect. It is reported as one
 * grouped warning, whatever the browser calls it, so it stays countable
 * without opening an error issue per browser wording.
 */
export function reportBoundaryError(error: Error) {
  if (isTransientNetworkError(error)) {
    Sentry.captureException(error, {
      level: 'warning',
      fingerprint: ['boundary-transient-network-error'],
      tags: { source: 'boundary-network' },
    })
    return
  }
  Sentry.captureException(error, { tags: { source: 'boundary' } })
}

export function useReportError(error: Error & { digest?: string }) {
  useEffect(() => {
    reportBoundaryError(error)
  }, [error])
}
